import { loadFacebookSdk } from './facebookSdk';

export type EmbeddedSignupConfig = {
  app_id: string;
  config_id: string;
  graph_version?: string;
  feature_type?: string;
};

export type EmbeddedSignupResult = {
  code: string;
  wabaId?: string;
  phoneNumberId?: string;
  coexistence: boolean;
};

type SignupSessionEvent = {
  type?: string;
  event?: string;
  data?: {
    waba_id?: string;
    phone_number_id?: string;
    current_step?: string;
    error_message?: string;
  };
};

type FacebookLoginExtras = {
  setup: Record<string, unknown>;
  sessionInfoVersion: string;
  featureType?: string;
};

const FINISH_EVENTS = [
  'FINISH',
  'FINISH_ONLY_WABA',
  'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING'
];

const parseJsonValue = (value: unknown): any => {
  if (typeof value !== 'string') {
    return value;
  }

  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const parseSignupEvent = (event: MessageEvent): SignupSessionEvent | null => {
  const origin = String(event.origin || '');
  if (!origin.includes('facebook.com')) {
    return null;
  }

  const parsed = parseJsonValue(event.data);
  if (!parsed || typeof parsed !== 'object') {
    return null;
  }

  const nested = parseJsonValue(parsed.data);
  const payload = nested && typeof nested === 'object' && (nested.type || nested.event)
    ? nested
    : parsed;

  if (payload.type && payload.type !== 'WA_EMBEDDED_SIGNUP') {
    return null;
  }

  const data = payload.data && typeof payload.data === 'object' ? payload.data : payload;
  const hasAssets = Boolean(data?.waba_id || data?.phone_number_id || payload.event);

  if (!hasAssets) {
    return null;
  }

  return {
    type: payload.type || 'WA_EMBEDDED_SIGNUP',
    event: payload.event,
    data: {
      waba_id: data.waba_id || payload.waba_id,
      phone_number_id: data.phone_number_id || payload.phone_number_id,
      current_step: data.current_step,
      error_message: data.error_message
    }
  };
};

export const launchWhatsAppEmbeddedSignup = (
  config: EmbeddedSignupConfig
): Promise<EmbeddedSignupResult> => {
  return new Promise(async (resolve, reject) => {
    try {
      await loadFacebookSdk(config.app_id, config.graph_version || 'v25.0');
    } catch (error) {
      reject(error);
      return;
    }

    if (!window.FB) {
      reject(new Error('SDK da Meta não está disponível'));
      return;
    }

    let code: string | undefined;
    let wabaId: string | undefined;
    let phoneNumberId: string | undefined;
    let coexistence = false;
    let settled = false;
    let missingAssetsTimeout: number | undefined;

    const cleanup = () => {
      window.removeEventListener('message', onMessage);
      window.clearTimeout(timeoutId);
      if (missingAssetsTimeout) {
        window.clearTimeout(missingAssetsTimeout);
      }
    };

    const settle = (handler: () => void) => {
      if (settled) {
        return;
      }
      settled = true;
      cleanup();
      handler();
    };

    const finishIfReady = (allowCodeOnly = false) => {
      const resolvedCode = code;
      const resolvedWabaId = wabaId;
      const resolvedPhoneNumberId = phoneNumberId;
      const hasAccount = Boolean(resolvedWabaId || resolvedPhoneNumberId);

      if (!resolvedCode || (!hasAccount && !allowCodeOnly)) {
        return;
      }

      settle(() => {
        resolve({
          code: resolvedCode,
          wabaId: resolvedWabaId,
          phoneNumberId: resolvedPhoneNumberId,
          coexistence
        });
      });
    };

    const onMessage = (event: MessageEvent) => {
      const payload = parseSignupEvent(event);
      if (!payload) {
        return;
      }

      const eventName = String(payload.event || '').toUpperCase();

      if (payload.data?.waba_id) {
        wabaId = String(payload.data.waba_id);
      }
      if (payload.data?.phone_number_id) {
        phoneNumberId = String(payload.data.phone_number_id);
      }
      if (eventName === 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING') {
        coexistence = true;
      }

      if (eventName === 'CANCEL') {
        settle(() => {
          reject(new Error('Cadastro do WhatsApp cancelado'));
        });
        return;
      }

      if (eventName === 'ERROR') {
        settle(() => {
          reject(new Error(payload.data?.error_message || 'Erro no cadastro incorporado da Meta'));
        });
        return;
      }

      if (!eventName || FINISH_EVENTS.includes(eventName)) {
        finishIfReady();
      }
    };

    window.addEventListener('message', onMessage);

    const timeoutId = window.setTimeout(() => {
      settle(() => {
        reject(new Error('Tempo esgotado ao conectar WhatsApp. Tente novamente.'));
      });
    }, 5 * 60 * 1000);

    const extras: FacebookLoginExtras = {
      setup: {},
      sessionInfoVersion: '3'
    };

    if (config.feature_type) {
      extras.featureType = config.feature_type;
    }

    window.FB.login(
      (response) => {
        if (response?.authResponse?.code) {
          code = response.authResponse.code;
          finishIfReady();
          missingAssetsTimeout = window.setTimeout(() => {
            if (!settled && code) {
              finishIfReady(true);
            }
          }, 8000);
          return;
        }

        window.setTimeout(() => {
          if (!settled) {
            settle(() => {
              reject(new Error('Cadastro do WhatsApp cancelado ou não autorizado'));
            });
          }
        }, 1500);
      },
      {
        config_id: config.config_id,
        response_type: 'code',
        override_default_response_type: true,
        extras
      }
    );
  });
};
